import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DataTable
} from '@tonic/shared/components/ui';
import { Plus, Search, MoreVertical, ArrowUpDown } from 'lucide-react';
import { formatCurrency, formatDate } from '@tonic/shared/utils/core/format';

// Mock IKEA resale data
const mockResaleItems = [
  {
    id: "RS-BILLY001",
    name: "BILLY Bookcase",
    category: "Storage",
    status: "Listed",
    resalePrice: 39.99,
    originalPrice: 79.99,
    condition: "Good",
    listedAt: "2024-03-15",
    description: "White bookcase, minor wear on corners"
  },
  {
    id: "RS-POANG002",
    name: "POÄNG Armchair",
    category: "Seating",
    status: "Sold",
    resalePrice: 89.99,
    originalPrice: 199.99,
    condition: "Excellent",
    listedAt: "2024-03-14",
    description: "Birch frame with beige cushions"
  },
  {
    id: "RS-MALM003",
    name: "MALM Bed Frame",
    category: "Bedroom",
    status: "Assessing",
    resalePrice: 149.99,
    originalPrice: 299.99,
    condition: "Good",
    listedAt: "2024-03-13",
    description: "Queen size, white, includes slats"
  },
  {
    id: "RS-KALLAX004",
    name: "KALLAX Shelf Unit",
    category: "Storage",
    status: "Repairing",
    resalePrice: 45.99,
    originalPrice: 89.99,
    condition: "Fair",
    listedAt: "2024-03-12",
    description: "4x4 unit, needs minor repairs"
  },
  {
    id: "RS-LACK005",
    name: "LACK Coffee Table",
    category: "Tables",
    status: "Quality_Check",
    resalePrice: 15.99,
    originalPrice: 29.99,
    condition: "Good",
    listedAt: "2024-03-11",
    description: "Black-brown, light scratches"
  },
  {
    id: "RS-HEMNES006",
    name: "HEMNES Dresser",
    category: "Storage",
    status: "Listed",
    resalePrice: 179.99,
    originalPrice: 349.99,
    condition: "Excellent",
    listedAt: "2024-03-10",
    description: "8-drawer, white stain, like new"
  },
  {
    id: "RS-FRIHETEN007",
    name: "FRIHETEN Sofa Bed",
    category: "Seating",
    status: "Received",
    resalePrice: 299.99,
    originalPrice: 599.99,
    condition: "Good",
    listedAt: "2024-03-09",
    description: "Grey, convertible, minor wear"
  },
  {
    id: "RS-ALEX008",
    name: "ALEX Desk",
    category: "Office",
    status: "Listed",
    resalePrice: 79.99,
    originalPrice: 159.99,
    condition: "Excellent",
    listedAt: "2024-03-08",
    description: "White desk with drawers"
  }
];

const columns = [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/resale/items/${row.original.id}`}
        className="font-medium hover:underline"
      >
        {row.original.id}
      </Link>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => row.original.name,
  },
  {
    accessorKey: 'category',
    header: 'Category',
    cell: ({ row }) => (
      <Badge variant="outline">{row.original.category}</Badge>
    ),
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: ({ row }) => (
      <Badge variant="secondary">{row.original.condition}</Badge>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase();
      const variant = {
        listed: 'default',
        sold: 'default',
        shipped: 'default',
        completed: 'default',
        received: 'secondary',
        assessing: 'secondary',
        repairing: 'secondary',
        quality_check: 'secondary',
      }[status] || 'outline';

      return (
        <Badge variant={variant}>
          {row.original.status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'resalePrice',
    header: 'Resale Price',
    cell: ({ row }) => formatCurrency(row.original.resalePrice),
  },
  {
    accessorKey: 'originalPrice',
    header: 'Original Price',
    cell: ({ row }) => formatCurrency(row.original.originalPrice),
  },
  {
    accessorKey: 'listedAt',
    header: 'Listed Date',
    cell: ({ row }) => formatDate(row.original.listedAt),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link to={`/admin/re-commerce/resale/items/${row.original.id}`}>
              View Details
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export function ResaleItemsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: null,
    category: null,
  });
  const [currentPage, setCurrentPage] = React.useState(0);
  const [sortConfig, setSortConfig] = React.useState({ field: null, direction: 'asc' });
  const pageSize = 10;

  const filteredItems = React.useMemo(() => {
    let items = mockResaleItems.filter((item) => {
      const matchesSearch =
        !filters.search ||
        item.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.id.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = !filters.status || item.status === filters.status;
      const matchesCategory = !filters.category || item.category === filters.category;
      return matchesSearch && matchesStatus && matchesCategory;
    });

    // Apply sorting
    if (sortConfig.field) {
      items = [...items].sort((a, b) => {
        if (a[sortConfig.field] < b[sortConfig.field]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.field] > b[sortConfig.field]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return items;
  }, [filters, sortConfig]);

  // Apply pagination
  const paginatedItems = React.useMemo(() => {
    const start = currentPage * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, currentPage]);

  const totalPages = Math.ceil(filteredItems.length / pageSize);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? null : value,
    }));
    setCurrentPage(0); // Reset to first page when filters change
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Resale Items</h1>
          <p className="text-sm text-gray-500">Manage your resale inventory</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Item
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search items..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="max-w-sm"
          />
        </div>
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) => handleFilterChange('status', value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Listed">Listed</SelectItem>
            <SelectItem value="Sold">Sold</SelectItem>
            <SelectItem value="Assessing">Assessing</SelectItem>
            <SelectItem value="Repairing">Repairing</SelectItem>
            <SelectItem value="Quality_Check">Quality Check</SelectItem>
            <SelectItem value="Received">Received</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.category || 'all'}
          onValueChange={(value) => handleFilterChange('category', value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="Storage">Storage</SelectItem>
            <SelectItem value="Seating">Seating</SelectItem>
            <SelectItem value="Bedroom">Bedroom</SelectItem>
            <SelectItem value="Tables">Tables</SelectItem>
            <SelectItem value="Office">Office</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <DataTable
              columns={columns}
              data={paginatedItems}
            />
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, filteredItems.length)} of {filteredItems.length} items
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage === totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 